import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SavedItemCard = ({ item, onRemove }) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const navigate = useNavigate();

  const handleRemove = async () => {
    setIsRemoving(true);
    await onRemove(item?.id);
    setIsRemoving(false);
  };

  const handleViewDetails = () => {
    navigate('/business-detail', { state: { businessId: item?.id } });
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    if (typeof price === 'string') return price;
    return `£${price}`;
  };

  return (
    <div className="bg-background border border-border rounded-lg overflow-hidden shadow-organic hover:shadow-organic-md transition-organic">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={item?.image}
          alt={item?.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3">
          <button
            onClick={handleRemove}
            disabled={isRemoving}
            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-organic hover:bg-white transition-gentle"
            aria-label="Remove from saved"
          >
            {isRemoving ? (
              <div className="w-4 h-4 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="Heart" size={20} color="#EF4444" className="fill-current" />
            )}
          </button>
        </div>
        {item?.type && (
          <div className="absolute top-3 left-3">
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
              {item?.type}
            </span>
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-foreground text-lg leading-tight">
            {item?.name}
          </h3>
          {item?.businessLogo && (
            <div className="w-8 h-8 rounded-full overflow-hidden ml-2 flex-shrink-0">
              <Image
                src={item?.businessLogo}
                alt={`${item?.businessName} logo`}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>

        {item?.businessName && (
          <p className="text-sm text-muted-foreground mb-2">
            by {item?.businessName}
          </p>
        )}

        <p className="text-sm text-foreground mb-3 line-clamp-2">
          {item?.description}
        </p>

        {/* Location and Price */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center text-sm text-muted-foreground">
            <Icon name="MapPin" size={14} className="mr-1" />
            {item?.location}
          </div>
          <div className="text-sm font-medium text-primary">
            {formatPrice(item?.price)}
          </div>
        </div>

        {/* Categories */}
        {item?.categories && item?.categories?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {item?.categories?.slice(0, 2)?.map((category, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full"
              >
                {category}
              </span>
            ))}
            {item?.categories?.length > 2 && (
              <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                +{item?.categories?.length - 2} more
              </span>
            )}
          </div>
        )}

        {/* Date saved */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Saved {item?.savedDate}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewDetails}
            iconName="ExternalLink"
            iconPosition="right"
            iconSize={14}
          >
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SavedItemCard;